import { PostFunction } from "src/utils/axios_connection";
import { removeUserInfo, setDataPrivileges, setUserInfo } from "./reducerUser";
import UrlApi from "src/utils/Url";
import { setLoading } from "src/store/customizer/CustomizerSlice";
import {
  dataLocalStorage,
  getLocalStorage,
  removeAllLocalStorage,
  saveLocalStorage,
} from "src/helper/publicFunction";
import { RouterName } from "src/routes/RouterName";
import { loginPath, rememberSessionEnded } from "src/views/authentication/session";

export const login =
  ({ email, password, expire, callback }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const data = {
        email,
        password,
        expire: expire ? 30 : 1,
      };
      // The form says what went wrong itself, in one message (no toast).
      const response = await PostFunction({
        data,
        Route: UrlApi.auth.login,
        showalert: false,
        showerror: false,
        state: getState,
        isMultipart: false,
        dispatch: dispatch,
      });
      saveLocalStorage(dataLocalStorage.userinfo, response?.data);
      saveLocalStorage(
        dataLocalStorage.privileges,
        response?.data?.dataPrivileges
      );
      dispatch(setUserInfo(response?.data));
      dispatch(setDataPrivileges(response?.data?.dataPrivileges));
      dispatch(setLoading(false));
      callback(true);
    } catch (err) {
      dispatch(setLoading(false));
      callback(false, err);
    }
  };

// Refreshes the saved session. Single-flight: however many callers ask at
// once (App and pages opening quickly one after another), one request goes
// out, its answer is stored once, and every caller hears back. A reply
// without a token leaves the saved session as it was.
let autologinInFlight = null;

export const Autologin =
  ({ callback = () => {} } = {}) =>
  async (dispatch, getState) => {
    if (!autologinInFlight) {
      autologinInFlight = PostFunction({
        data: {},
        Route: UrlApi.auth.autologin,
        showalert: false,
        // A background check: a failure here is not something the person
        // did, and the page they are on says its own errors.
        showerror: false,
        state: getState,
        isMultipart: false,
        dispatch: dispatch,
      })
        .then((response) => {
          if (response?.data?.token) {
            saveLocalStorage(dataLocalStorage.userinfo, response.data);
            saveLocalStorage(dataLocalStorage.privileges, response.data.dataPrivileges);
            dispatch(setUserInfo(response.data));
            dispatch(setDataPrivileges(response.data.dataPrivileges));
          }
          return response;
        })
        .catch(() => false)
        .finally(() => {
          autologinInFlight = null;
        });
    }
    await autologinInFlight;
    callback();
  };

export const signup =
  ({ email, password, phone, username, fullname, user_type_id }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const data = {
        email,
        password,
        phone,
        username,
        fullname,
        user_type_id,
      };
      const response = await PostFunction({
        data,
        Route: UrlApi.auth.signup,
        showalert: true,
        state: getState,
        isMultipart: false,
        dispatch: dispatch,
      });
      dispatch(setLoading(false));
    } catch (err) {}
  };

export const Logout =
  ({ navigate = null }) =>
  async (dispatch, getState) => {
    try {
      const data = getLocalStorage(dataLocalStorage.userinfo);
      if (data.token) {
        dispatch(setLoading(true));
        const response = await PostFunction({
          data: {},
          Route: UrlApi.auth.logout,
          showalert: "You’re signed out.",
          state: getState,
          isMultipart: false,
          dispatch: dispatch,
        });
        if (response) {
          removeAllLocalStorage();
          dispatch(removeUserInfo());
          dispatch(setLoading(false));
          if (navigate) {
            navigate(RouterName.auth.login);
          } else {
            window.location.reload(false);
          }
        } else {
          dispatch(setLoading(false));
        }
      } else {
        removeAllLocalStorage();
        dispatch(removeUserInfo());
        if (navigate) {
          navigate(RouterName.auth.login);
        } else {
          window.location.reload(false);
        }
      }
    } catch (err) {
      removeAllLocalStorage();
      dispatch(removeUserInfo());
      if (navigate) {
        navigate(RouterName.auth.login);
      } else {
        window.location.reload(false);
      }
      dispatch(setLoading(false));
    }
  };

// The saved session was refused (401): it has expired. Nothing is sent to
// the server (logging out an expired token only fails); the sign-in page
// says why and brings the person back to this page afterwards (QA F11).
export const SessionEnded = () => (dispatch) => {
  const signedIn = !!getLocalStorage(dataLocalStorage.userinfo)?.token;
  if (!signedIn) return;
  removeAllLocalStorage();
  dispatch(removeUserInfo());
  dispatch(setLoading(false));
  rememberSessionEnded();
  window.location.assign(loginPath(window.location.pathname + window.location.search));
};

export const ForgotPasswordFunction =
  ({ email, callback }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const fullUrl = window.location.href;
      const parsedUrl = new URL(fullUrl);
      const baseUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}:${parsedUrl.port}`;
      const response = await PostFunction({
        data: {
          email,
          currentDomain: baseUrl,
        },
        Route: UrlApi.auth.forgotPassword,
        showalert: true,
        state: getState,
        isMultipart: false,
        dispatch: dispatch,
      });
      if (response) {
        dispatch(setLoading(false));
        callback(response);
      } else {
        dispatch(setLoading(false));
        callback(false);
      }
    } catch (err) {
      dispatch(setLoading(false));
    }
  };

export const ResetPasswordFunction =
  ({ token, newPassword, callback }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const response = await PostFunction({
        data: {
          newPassword,
          token,
        },
        Route: UrlApi.auth.resetPassword,
        showalert: true,
        state: getState,
        isMultipart: false,
        dispatch: dispatch,
      });
      if (response) {
        dispatch(setLoading(false));
        callback(response);
      } else {
        dispatch(setLoading(false));
        callback(false);
      }
    } catch (err) {
      dispatch(setLoading(false));
    }
  };
